// Pure helper functions for the Monitoring-by-group redesign (MIC-4128 block A,
// story 35-monitoring-screen-redesign). No React, no apiGet: independently testable
// business rules for group navigation (RN6/RN7), quick-filter/group intersection
// (RN8/RN9/V2/V3) and the per-type "operative" summary (RN15).
//
// The canonical status(by-type) -> bucket classification rule lives in sun's domain
// (RN3): sun SHIPS (built 2026-07-07, sun-contract-proposal.md section 2) the
// already-computed bucket as the additive `statusBucket` field on
// `monitoring/deviceSummary` items. Jupiter MUST NOT reimplement that rule:
// `getDeviceBucket` only reads the field sun sends. When the field is absent
// (older sun) the device degrades to the residual bucket — safe because the
// residual is only ever visible under the "all"/OFF filters (RN8/V2).
//
// Domain correction (PO, 2026-07-07): NON_OPERATIVE is a device MODE, not a status;
// the residual STATUS is OFF. The internal identifier stays NON_OPERATIVE (it
// matches sun's `nonOperativeCount` KPI field), and `normalizeSunBucket` accepts
// 'OFF' as an alias in case sun adopts the corrected vocabulary for the bucket.

const RESIDUAL_BUCKET = 'NON_OPERATIVE'
const STATUS_BUCKETS = ['WORKING', 'IDLE', 'ERROR', RESIDUAL_BUCKET]
const SUN_BUCKET_ALIASES = { OFF: RESIDUAL_BUCKET }

// Quick filters mirror the REAL device states (PO): OFF(0), IDLE(1), WORKING(2),
// ERROR — plus "all".
const QUICK_FILTERS = { ALL: 'ALL', WORKING: 'WORKING', IDLE: 'IDLE', ERROR: 'ERROR', OFF: 'OFF' }

const NO_GROUP_ID = 'NO_GROUP'
const ALL_GROUPS_ID = 'ALL'

/**
 * Normalises the `statusBucket` value sun sends: a canonical bucket passes through,
 * the 'OFF' alias maps to the residual bucket (domain correction, see module
 * header), anything else — absent field included — yields '' (no sun bucket).
 * This is normalisation of sun's own vocabulary, NOT reclassification (RN3).
 * @param {string|undefined} value
 * @returns {'WORKING'|'IDLE'|'ERROR'|'NON_OPERATIVE'|''}
 */
function normalizeSunBucket (value) {
  if (STATUS_BUCKETS.includes(value)) return value
  return SUN_BUCKET_ALIASES[value] ?? ''
}

/**
 * The single point where jupiter reads the bucket sun computed (RN3). Never
 * reclassifies by itself; an absent/unrecognised value degrades to the residual
 * bucket (see module header).
 * @param {{statusBucket?: string}} device
 * @returns {'WORKING'|'IDLE'|'ERROR'|'NON_OPERATIVE'}
 */
function getDeviceBucket (device) {
  const bucket = normalizeSunBucket(device?.statusBucket)
  return bucket !== '' ? bucket : RESIDUAL_BUCKET
}

/**
 * Which quick-filter key a device belongs to. Prefers sun's canonical bucket
 * (shipped 2026-07-07; the residual bucket maps to the OFF filter); when the field
 * is absent (older sun), falls back to the device's REAL status — OFF(0), IDLE(1),
 * WORKING(2), ERROR (PO directive) — with unknown values under OFF.
 * @param {{statusBucket?: string, status?: string}} device
 * @returns {'WORKING'|'IDLE'|'ERROR'|'OFF'}
 */
function getDeviceFilterKey (device) {
  const bucket = normalizeSunBucket(device?.statusBucket)
  if (bucket !== '') {
    return bucket === RESIDUAL_BUCKET ? QUICK_FILTERS.OFF : bucket
  }
  const status = device?.status ?? ''
  if (status === QUICK_FILTERS.WORKING || status === QUICK_FILTERS.IDLE || status === QUICK_FILTERS.ERROR) return status
  return QUICK_FILTERS.OFF
}

function isGroup (item) {
  return item?.type === 'GROUP'
}

// Identity-stable flatten cache: the same source device object always yields the same
// flattened (clone + groupId tag) object. `flattenDevices` runs several times per render
// (sections + filter counts), and without this every pass would mint fresh clones —
// defeating any React.memo on the cards downstream. WeakMap-keyed by the source object,
// so entries are garbage-collected with their snapshots.
const flattenedDeviceCache = new WeakMap()
function flattenOne (device, groupId) {
  const cached = flattenedDeviceCache.get(device)
  if (cached && cached.groupId === groupId) return cached
  const flattened = { ...device, groupId }
  flattenedDeviceCache.set(device, flattened)
  return flattened
}

/**
 * Flattens `monitoring/deviceSummary` items (groups + top-level devices) into a
 * single list of devices, each tagged with the group (group id) it belongs to —
 * `NO_GROUP_ID` for devices with no group (RN7). Flattened objects keep a stable
 * identity per source device object (see cache above).
 * @param {Array} items
 * @returns {Array}
 */
function flattenDevices (items) {
  const flat = []
  ;(items ?? []).forEach((item) => {
    if (isGroup(item)) {
      (item.devices ?? []).forEach((device) => flat.push(flattenOne(device, item.id)))
    } else {
      flat.push(flattenOne(item, NO_GROUP_ID))
    }
  })
  return flat
}

/**
 * Builds the group navigator entries (RN6/RN7): one per existing group, plus a
 * "no group" section when at least one device does not belong to any group.
 * @param {Array} items
 * @returns {Array<{id: string|number, name: string, type: 'GROUP'|'NO_GROUP'}>}
 */
function buildDeviceGroups (items) {
  const groupCells = (items ?? [])
    .filter(isGroup)
    .map((group) => ({ id: group.id, name: group.name, type: 'GROUP' }))
  const hasUngrouped = (items ?? []).some((item) => !isGroup(item))
  return hasUngrouped
    ? [...groupCells, { id: NO_GROUP_ID, name: 'No group', type: 'NO_GROUP' }]
    : groupCells
}

function matchesGroup (device, groupId) {
  if (groupId === ALL_GROUPS_ID) return true
  return String(device.groupId) === String(groupId)
}

function matchesFilter (device, filterKey) {
  if (filterKey === QUICK_FILTERS.ALL) return true
  return getDeviceFilterKey(device) === filterKey
}

/**
 * The group x quick-filter intersection (RN9): 100% client-side, over the devices
 * already loaded in the current refresh tick. Never re-queries the backend.
 * @param {Array} items `monitoring/deviceSummary` items (groups + top-level devices)
 * @param {string|number} groupId `ALL_GROUPS_ID`, a group id, or `NO_GROUP_ID`
 * @param {string} filterKey one of `QUICK_FILTERS`
 * @returns {Array} devices visible in the grid
 */
function selectVisibleDevices (items, groupId, filterKey) {
  return flattenDevices(items).filter((device) => matchesGroup(device, groupId) && matchesFilter(device, filterKey))
}

/**
 * Per-type "operative" summary for the group compact card (HU-06, RN15): a type is
 * marked "on" as soon as one of its devices is NOT in the residual bucket. Reuses
 * `getDeviceBucket`, never reclassifies on its own.
 * @param {Array} devices
 * @returns {Array<{type: string, on: boolean}>}
 */
function summarizeDeviceTypes (devices) {
  const byType = {}
  ;(devices ?? []).forEach((device) => {
    const type = device?.deviceType?.name ?? 'UNKNOWN'
    if (!(type in byType)) byType[type] = { type, on: false }
    if (getDeviceBucket(device) !== RESIDUAL_BUCKET) byType[type].on = true
  })
  return Object.values(byType)
}

export {
  QUICK_FILTERS,
  RESIDUAL_BUCKET,
  STATUS_BUCKETS,
  NO_GROUP_ID,
  ALL_GROUPS_ID,
  getDeviceBucket,
  getDeviceFilterKey,
  normalizeSunBucket,
  flattenDevices,
  buildDeviceGroups,
  selectVisibleDevices,
  summarizeDeviceTypes
}
